import { decodeMessage, encodeMessage, type NetworkMessage, type NetworkPlayerState } from "./protocol";
import { createSignalingClient, type SignalingClient, type SignalMessage } from "./signaling";
import type { GameModeId } from "../levels/types";

export interface CortexClientOptions {
  serverId: string;
  hostPeerId: string;
  clientPeerId: string;
  playerName: string;
  team?: "alpha" | "bravo";
  iceServers?: RTCIceServer[];
  onPlayersChange?: (players: NetworkPlayerState[]) => void;
  onMessage?: (message: NetworkMessage) => void;
  onError?: (message: string) => void;
  onMatchStart?: (payload: { mode: GameModeId; mapId: string }) => void;
  onHostQuit?: () => void;
}

export class CortexWebRtcClient {
  private signaling: SignalingClient | null = null;
  private connection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private started = false;
  private players: NetworkPlayerState[] = [];

  constructor(private options: CortexClientOptions) {}

  getPlayers() {
    return this.players;
  }

  async start() {
    if (this.started) return;
    this.started = true;
    this.signaling = createSignalingClient({
      serverId: this.options.serverId,
      peerId: this.options.clientPeerId,
      role: "client",
      playerName: this.options.playerName,
      onSignal: (message) => {
        void this.handleSignal(message);
      },
    });

    await this.signaling.send({
      kind: "join",
      to: this.options.hostPeerId,
      payload: {
        playerName: this.options.playerName,
        team: this.options.team,
      },
    });
  }

  private ensureConnection() {
    if (this.connection) {
      return this.connection;
    }

    const connection = new RTCPeerConnection({ iceServers: this.options.iceServers });
    connection.onicecandidate = (event) => {
      if (!this.signaling) return;
      void this.signaling.send({
        kind: "ice",
        to: this.options.hostPeerId,
        payload: { candidate: event.candidate?.toJSON() ?? null },
      });
    };

    connection.ondatachannel = (event) => {
      this.dataChannel = event.channel;
      this.dataChannel.onmessage = (messageEvent) => {
        try {
          const message = decodeMessage(String(messageEvent.data));
          if (message.t === "c" && message.c === "player-list") {
            this.players = (message.payload?.players as NetworkPlayerState[]) ?? [];
            this.options.onPlayersChange?.(this.players);
          }
          if (message.t === "c" && message.c === "start-match") {
            this.options.onMatchStart?.({
              mode: (message.payload?.mode as GameModeId) ?? "dm",
              mapId: String(message.payload?.mapId ?? "arena01"),
            });
          }
          if (message.t === "c" && message.c === "host-quit") {
            this.options.onHostQuit?.();
          }
          this.options.onMessage?.(message);
        } catch (error) {
          this.options.onError?.(error instanceof Error ? error.message : "Failed to decode host message.");
        }
      };
    };

    connection.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(connection.connectionState)) {
        this.options.onHostQuit?.();
      }
    };

    this.connection = connection;
    return connection;
  }

  private async handleSignal(message: SignalMessage) {
    const connection = this.ensureConnection();

    if (message.kind === "offer") {
      await connection.setRemoteDescription(message.payload.sdp as RTCSessionDescriptionInit);
      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);
      await this.signaling?.send({
        kind: "answer",
        to: this.options.hostPeerId,
        payload: { sdp: answer },
      });
      return;
    }

    if (message.kind === "ice") {
      const candidate = message.payload.candidate as RTCIceCandidateInit | null;
      if (!candidate) return;
      await connection.addIceCandidate(candidate);
    }
  }

  send(message: NetworkMessage) {
    if (this.dataChannel?.readyState !== "open") return;
    this.dataChannel.send(encodeMessage(message));
  }

  destroy() {
    this.dataChannel?.close();
    this.connection?.close();
    this.signaling?.close();
    this.dataChannel = null;
    this.connection = null;
    this.signaling = null;
    this.players = [];
    this.started = false;
  }
}